import React, { useEffect, useState } from 'react'
import { Container, Row, Col, Form, Button } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import DOMPurify from 'dompurify'
import { translations } from '../i18n'
import { getPost, addComment, toggleReadLater } from '../actions/blogActions'
import { COMMENT_RESET } from '../constants/blogConstants'
import Loader from '../components/Loader'
import Message from '../components/Message'

function PostDetailScreen() {
  const { slug } = useParams()
  const dispatch = useDispatch()
  const { lang } = useSelector(s => s.rootReducer.langRed)
  const T = translations[lang]
  const { loading, error, post } = useSelector(s => s.rootReducer.postRed)
  const { loading: cLoading, success: cSuccess, error: cError } = useSelector(s => s.rootReducer.commentRed)
  const { ids: savedIds = [] } = useSelector(s => s.rootReducer.readLaterRed)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [text, setText] = useState('')

  useEffect(() => {
    dispatch(getPost(slug))
    dispatch({ type: COMMENT_RESET })
  }, [dispatch, slug])

  const submitComment = (e) => {
    e.preventDefault()
    dispatch(addComment(slug, { user_name: name, user_email: email, text }))
    setName(''); setEmail(''); setText('')
  }

  if (loading) return <div className='fc-section'><Container><Loader /></Container></div>
  if (error)   return <div className='fc-section'><Container><Message variant='danger'>{error}</Message></Container></div>
  if (!post)   return null

  const title   = post[`title_${lang}`]   || post.title_en
  const content = post[`content_${lang}`] || post.content_en
  const saved   = savedIds.includes(post.id)

  return (
    <div>
      {/* Header */}
      <div className='post-header'>
        <Container>
          <Link to='/blog/' style={{ color: 'var(--teal)', fontSize: '.88rem' }}>{T.blog.back}</Link>
          <h1 style={{ marginTop: '.75rem' }}>{title}</h1>
          <div className='post-meta'>
            {post.author?.first_name} {post.author?.last_name} &nbsp;·&nbsp;
            {new Date(post.date).toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
            {(post.tags || []).map(tag => <span key={tag.id} className='tag-pill ms-2'>{tag.caption}</span>)}
          </div>
        </Container>
      </div>

      <div className='fc-section'>
        <Container>
          <Row>
            <Col lg={8}>
              {/* Content */}
              {post.image && <img src={post.image} alt={title} style={{ width: '100%', borderRadius: 'var(--radius)', marginBottom: '2rem' }} />}
              <div className='post-content' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />

              {/* Read later */}
              <div style={{ margin: '1.5rem 0', display: 'flex', justifyContent: 'flex-end' }}>
                <button className={`btn-read-later ${saved ? 'saved' : ''}`} onClick={() => dispatch(toggleReadLater(post.id))}>
                  {saved ? T.blog.remove : T.blog.save}
                </button>
              </div>

              {/* Comments */}
              <hr style={{ borderColor: 'var(--border)', margin: '2rem 0' }} />
              <h4 style={{ color: 'var(--navy)', marginBottom: '1.25rem' }}>{T.blog.comments} ({(post.comments || []).length})</h4>
              {(post.comments || []).length === 0
                ? <p style={{ color: 'var(--muted)' }}>{T.blog.no_comments}</p>
                : post.comments.map(c => (
                  <div className='comment-card' key={c.id}>
                    <span className='comment-author'>{c.user_name}</span>
                    <span className='comment-date'>{new Date(c.created_at).toLocaleDateString()}</span>
                    <div className='comment-text'>{c.text}</div>
                  </div>
                ))
              }

              {/* Add comment form */}
              <div style={{ marginTop: '2rem', background: 'var(--bg)', borderRadius: 'var(--radius)', padding: '1.5rem' }}>
                <h5 style={{ color: 'var(--navy)', marginBottom: '1rem' }}>{T.blog.add_comment}</h5>
                {cSuccess && <Message variant='success'>{T.blog.thanks}</Message>}
                {cError   && <Message variant='danger'>{cError}</Message>}
                {!cSuccess && (
                  <Form onSubmit={submitComment}>
                    <Row>
                      <Col sm={6}>
                        <Form.Group className='mb-3'>
                          <Form.Label>{T.blog.name_label}</Form.Label>
                          <Form.Control required value={name} onChange={e => setName(e.target.value)} />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group className='mb-3'>
                          <Form.Label>{T.blog.email_label}</Form.Label>
                          <Form.Control type='email' value={email} onChange={e => setEmail(e.target.value)} />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className='mb-3'>
                      <Form.Label>{T.blog.text_label}</Form.Label>
                      <Form.Control as='textarea' rows={4} required value={text} onChange={e => setText(e.target.value)} />
                    </Form.Group>
                    <Button type='submit' style={{ background: 'var(--teal)', border: 'none' }} disabled={cLoading}>
                      {T.blog.submit}
                    </Button>
                  </Form>
                )}
              </div>
            </Col>

            {/* Sidebar: author */}
            {post.author && (
              <Col lg={4}>
                <div style={{ position: 'sticky', top: '80px', background: 'var(--bg)', borderRadius: 'var(--radius)', padding: '1.5rem', textAlign: 'center' }}>
                  {post.author.photo
                    ? <img src={post.author.photo} alt={post.author.first_name} style={{ width: 80, height: 80, borderRadius: '50%', marginBottom: '1rem' }} />
                    : <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontSize: '1.8rem', color: '#fff' }}>👤</div>
                  }
                  <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{post.author.first_name} {post.author.last_name}</div>
                  <div style={{ fontSize: '.85rem', color: 'var(--muted)', marginTop: '.5rem' }}>
                    {post.author[`bio_${lang}`] || post.author.bio_en}
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </Container>
      </div>
    </div>
  )
}

export default PostDetailScreen
